"use client";

import { useEffect, useState } from "react";
import { onSnapshot, query, where } from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import { goalSchema, type Goal } from "@/lib/schemas";

type State = { goals: Goal[]; loading: boolean; error: string | null };

const cache = new Map<string, Goal[]>();

/**
 * Cele rodziny — kind in [family_count, family_offline_count].
 * Tworzy/edytuje tylko parent (egzekwowane przez firestore.rules).
 *
 * Postęp wyliczany z `useGoalProgress(goal)` (nie cache'ujemy w doc) —
 * source of truth = approved completions w HH.
 */
export function useHouseholdGoals(householdId: string | undefined): State {
  const cached = householdId ? cache.get(householdId) : undefined;
  const [state, setState] = useState<State>({
    goals: cached ?? [],
    loading: cached === undefined,
    error: null,
  });

  useEffect(() => {
    if (!householdId) return;
    const db = getDb();
    const q = query(
      refCol(db, paths.goals(householdId)),
      where("kind", "in", ["family_count", "family_offline_count"]),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const goals = snap.docs
          .flatMap((d) => {
            const parsed = goalSchema.safeParse({ id: d.id, ...d.data() });
            return parsed.success ? [parsed.data] : [];
          })
          .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
        cache.set(householdId, goals);
        setState({ goals, loading: false, error: null });
      },
      (err) => setState({ goals: [], loading: false, error: err.message }),
    );
    return () => unsub();
  }, [householdId]);

  return state;
}
